/**
 * As refernce Shopify documentation
 * https://shopify.dev/docs/storefronts/themes/product-merchandising/gift-cards
 *
 */

import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';

import Button, { TYPES } from 'components/Button';
import ButtonSwitch from 'components/ButtonSwitch';
import { TertiaryHeading } from 'components/Headings';
import Input from 'components/Input';
import Modal from 'components/Modal';
import TextArea from 'components/TextArea';
import {
  MODAL_CLOSE_TIMEOUT_MS,
  INPUT_EMPTY_TEXT,
  INPUT_EMPTY_DATE,
  INPUT_DATE_PATTERN,
  INPUT_NAME_MAX_LENGTH,
  TEXTAREA_MAX_LENGTH,
  TEXTAREA_MAX_HEIGHT,
} from 'constants/digitalGiftCard';
import I18n from 'utils/I18n';

import {
  buildGiftCardLineItemProperties,
  currentDateYYYYMMDDFormat,
  maxPermitedDateYYYYMMDDFormat,
  nameValidator,
  emailValidator,
  dateValidator,
  messageValidator,
} from './utils';

const PdpSendAsGiftCardForm = ({ isModalOpen, onCloseModal, onOpenModal, handleAddToCart }) => {
  const [isOpen, setIsOpen] = useState(isModalOpen);
  const [isEmailValid, setIsEmailValid] = useState(null);
  const [isMessageValid, setIsMessageValid] = useState(null);
  const [isSendOnValid, setIsSendOnValid] = useState(null);
  const [isNameValid, setIsNameValid] = useState(null);
  const [isSendOnNowActive, setIsSendOnNowActive] = useState(true);
  const [emailRecipient, setEmailRecipient] = useState(INPUT_EMPTY_TEXT);
  const [nameRecipient, setNameRecipient] = useState(INPUT_EMPTY_TEXT);
  const [messageRecipient, setMessageRecipient] = useState(INPUT_EMPTY_TEXT);
  const [sendOn, setSendOn] = useState(currentDateYYYYMMDDFormat());

  const resetFormInputs = () => {
    setEmailRecipient(INPUT_EMPTY_TEXT);
    setNameRecipient(INPUT_EMPTY_TEXT);
    setMessageRecipient(INPUT_EMPTY_TEXT);
    setSendOn(currentDateYYYYMMDDFormat());
    setIsEmailValid(null);
    setIsMessageValid(null);
    setIsSendOnValid(null);
    setIsNameValid(null);
    setIsSendOnNowActive(true);
  };

  const onRequestCloseModal = useCallback(() => {
    resetFormInputs();
    setIsOpen(false);
    onCloseModal();
  }, [onCloseModal]);

  const onModaAfterOpen = useCallback(() => {
    onOpenModal();
  }, [onOpenModal]);

  const onSendAsGiftClick = () => {
    setIsOpen(true);
  };

  const onAddGiftCardToCartClick = useCallback(
    event => {
      event?.preventDefault();

      const validEmail = emailValidator(emailRecipient);
      setIsEmailValid(validEmail);
      const validName = nameValidator(nameRecipient);
      setIsNameValid(validName);
      const validMessage = messageValidator(messageRecipient);
      setIsMessageValid(validMessage);
      const validSendOn = dateValidator(sendOn);
      setIsSendOnValid(validSendOn);

      if (validEmail && validName && validMessage && validSendOn) {
        handleAddToCart({
          lineItemProperties: buildGiftCardLineItemProperties(
            emailRecipient,
            nameRecipient,
            messageRecipient,
            sendOn
          ),
        });
        setIsOpen(false);
        resetFormInputs();
      }
    },
    [emailRecipient, handleAddToCart, messageRecipient, nameRecipient, sendOn]
  );

  const onEmailRecipientChange = emailParam => {
    setEmailRecipient(emailParam);
  };

  const onBlurEmailRecipient = () => {
    setIsEmailValid(emailValidator(emailRecipient));
  };

  const onNameRecipientChange = nameParam => {
    setNameRecipient(nameParam);
  };

  const onBlurnameRecipient = () => {
    setIsNameValid(nameValidator(nameRecipient));
  };

  const onMessageChange = messageParam => {
    setMessageRecipient(messageParam);
  };

  const onMessageBlur = () => {
    setIsMessageValid(messageValidator(messageRecipient));
  };

  const onSendOnChange = sendOnParam => {
    setSendOn(sendOnParam);
    setIsSendOnNowActive(() => sendOnParam == currentDateYYYYMMDDFormat());
    setIsSendOnValid(() => sendOnParam != INPUT_EMPTY_DATE);
  };

  const onSendOnNowClick = event => {
    event?.preventDefault();

    setSendOn(currentDateYYYYMMDDFormat());
    setIsSendOnNowActive(true);
    setIsSendOnValid(true);
  };

  return (
    <div className="PdpSendAsGiftCardForm">
      <div className="mt-4">
        <Button
          id="open-form-send-as-a-gift"
          type={TYPES.DEFAULT}
          onClick={onSendAsGiftClick}
          isUppercase
          isFullWidth
        >
          {I18n.localize('pdp.sendAsGiftForm.buttonText')}
        </Button>
      </div>

      <Modal
        className="PdpSendAsGiftCardForm__Modal rounded-md"
        overlayOpacity={0.5}
        onRequestClose={onRequestCloseModal}
        onAfterOpen={onModaAfterOpen}
        isOpen={isOpen}
        preventScroll
        closeTimeoutMS={MODAL_CLOSE_TIMEOUT_MS}
        aria-modal={true}
      >
        <div className="flex flex-col items-start sm:items-center">
          <div className="mb-10 self-center">
            <TertiaryHeading>{I18n.localize('pdp.sendAsGiftForm.headingText')}</TertiaryHeading>
          </div>
          <form className="grid max-h-[30rem] w-full min-w-40 max-w-[90%] grid-cols-[25%_75%]  grid-rows-6 items-start justify-start gap-x-8 gap-y-8 sm:min-w-80 sm:max-w-md">
            <label className="h-10 text-left text-lg font-bold" htmlFor="gitf-card-recipient-email">
              {I18n.localize('pdp.sendAsGiftForm.emailLabel')}
            </label>

            <div className="h-10 w-full">
              <Input
                id="gitf-card-recipient-email"
                inputType="email"
                isValid={isEmailValid}
                onChange={e => onEmailRecipientChange(e.target.value)}
                onBlur={onBlurEmailRecipient}
                value={emailRecipient}
                placeholder={I18n.localize('pdp.sendAsGiftForm.emailPlaceholder')}
                ariaLabel={I18n.localize('pdp.sendAsGiftForm.emailPlaceholder')}
              />
              {isEmailValid === false && (
                <label
                  className="block text-left text-xs font-bold text-error"
                  htmlFor="gitf-card-recipient-email"
                >
                  {I18n.localize('pdp.sendAsGiftForm.emailErrorText')}
                </label>
              )}
            </div>

            <div className="h-10" />
            <div className="h-10 w-full">
              <Input
                id="gitf-card-recipient-name"
                inputType="text"
                maxLength={INPUT_NAME_MAX_LENGTH}
                className="mt-2 w-full bg-white"
                onChange={e => onNameRecipientChange(e.target.value)}
                onBlur={onBlurnameRecipient}
                value={nameRecipient}
                isValid={isNameValid}
                placeholder={I18n.localize('pdp.sendAsGiftForm.namePlaceholder')}
                ariaLabel={I18n.localize('pdp.sendAsGiftForm.namePlaceholder')}
              />
              {isNameValid === false && (
                <label
                  className="block text-left text-xs font-bold text-error"
                  htmlFor="gitf-card-recipient-name"
                >
                  {I18n.localize('pdp.sendAsGiftForm.nameErrorText')}
                </label>
              )}
            </div>

            <label
              className="row-span-2 text-left text-lg font-bold"
              htmlFor="gitf-card-recipient-message"
            >
              {I18n.localize('pdp.sendAsGiftForm.messageLabel')}
            </label>
            <div className="row-span-2 flex flex-col">
              <TextArea
                id="gitf-card-recipient-message"
                isValid={isMessageValid}
                value={messageRecipient}
                height={TEXTAREA_MAX_HEIGHT}
                onChange={e => onMessageChange(e.target.value)}
                onBlur={onMessageBlur}
                className="w-full bg-white"
                maxLength={TEXTAREA_MAX_LENGTH}
                placeholder={I18n.localize('pdp.sendAsGiftForm.messagePlaceholder')}
                ariaLabel={I18n.localize('pdp.sendAsGiftForm.messagePlaceholder')}
              />
              {isMessageValid === false && (
                <label
                  className="text-errors block text-left text-xs font-bold text-error"
                  htmlFor="gitf-card-recipient-message"
                >
                  {I18n.localize('pdp.sendAsGiftForm.messageErrorText')}
                </label>
              )}
            </div>

            <label className="text-left text-lg font-bold" htmlFor="gitf-card-recipient-send-on">
              {I18n.localize('pdp.sendAsGiftForm.sendOnLabel')}
            </label>
            <div className="flex h-10 w-full flex-col gap-1">
              <div className="flex flex-row gap-2">
                <ButtonSwitch
                  id="send-on-now-send-as-a-gift"
                  isSwitched={isSendOnNowActive}
                  isValid={isSendOnValid}
                  onClick={onSendOnNowClick}
                  ariaLabel={I18n.localize('pdp.sendAsGiftForm.sendOnNowSwitchText')}
                >
                  {I18n.localize('pdp.sendAsGiftForm.sendOnNowSwitchText')}
                </ButtonSwitch>
                <div className="min-w-[55%]">
                  <Input
                    id="gitf-card-recipient-send-on"
                    inputType="date"
                    isValid={isSendOnValid}
                    onChange={e => onSendOnChange(e.target.value)}
                    value={sendOn}
                    pattern={INPUT_DATE_PATTERN}
                    min={currentDateYYYYMMDDFormat()}
                    max={maxPermitedDateYYYYMMDDFormat()}
                    ariaLabel={I18n.localize('pdp.sendAsGiftForm.sendOnLabel')}
                  />
                </div>
              </div>
              {isSendOnValid === false && (
                <label
                  className="text-errors block text-left text-xs font-bold text-error"
                  htmlFor="gitf-card-recipient-send-on"
                >
                  {I18n.localize('pdp.sendAsGiftForm.sendOnErrorText')}
                </label>
              )}
            </div>

            <div className="col-span-full mt-4 flex w-full flex-col items-center self-end">
              <Button
                id="add-to-cart-send-as-a-gift"
                type={TYPES.DEFAULT}
                onClick={onAddGiftCardToCartClick}
                isSmall
                isUppercase
                isSubmit
              >
                {I18n.localize('pdp.sendAsGiftForm.addToCartButtonText')}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

PdpSendAsGiftCardForm.propTypes = {
  isModalOpen: PropTypes.bool,
  onOpenModal: PropTypes.func,
  onCloseModal: PropTypes.func,
  handleAddToCart: PropTypes.func,
};

PdpSendAsGiftCardForm.defaultProps = {
  isModalOpen: false,
  onOpenModal: () => {},
  onCloseModal: () => {},
  handleAddToCart: () => {},
};

export default PdpSendAsGiftCardForm;
